import React, { useMemo, useState } from 'react'
import { ChevronUp } from 'lucide-react'
import { TimeManagementListItem } from '@myapp/contract'
import { useDispositionDraftStore } from '../model/useDispositionDraftStore'
import { useCollisionDetection } from '../../../shared/lib/collision'
import { useCollisionAwareDurations } from '../model/useCollisionAwareDurations'
import { buildTimedTasksByTechnician } from '../model/buildTimedTasksByTechnician'
import { useTechnicianAppointments } from '../../../entities/appointment'
import { useBreakBlocks } from '../../../entities/time-entry'
import { useTimelineTasks } from '../../../entities/task'
import { getDayRange } from '../../../shared/lib/blockRanges'
import { filterSchedulableTimeManagement } from '../../../shared/lib/timeline/weekHelpers'
import { DraftTaskForm } from './DraftTaskForm'

interface Props {
  taskTitle: string
  taskId: number
  onSave: () => void
  onCancel: () => void
  isPending: boolean
}

function formatWorkDateTime(iso: string) {
  const d = new Date(iso)
  const date = d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })
  const time = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
  return { date, time }
}

export const DraftTaskPanel: React.FC<Props> = ({ taskTitle, taskId, onSave, onCancel, isPending }) => {
  const draft = useDispositionDraftStore((s) => s.draft)
  const technicianName = useDispositionDraftStore((s) => s.technicianName)
  const ghost = useDispositionDraftStore((s) => s.ghost)
  const setDurations = useDispositionDraftStore((s) => s.setDurations)
  const durations = useDispositionDraftStore((s) => s.durations)

  // Derive dayIso from ghost position — avoids widget-layer dependency
  const anfahrtIso = useDispositionDraftStore((s) => s.ghost?.anfahrtStartIso)
  const dayIso = anfahrtIso ? anfahrtIso.slice(0, 10) : ''
  const range = getDayRange(anfahrtIso ?? new Date().toISOString())

  const { data } = useTechnicianAppointments({ from: range.from, to: range.to })
  const taskItems = useTimelineTasks(range.from, range.to)

  const breakBlocks = useBreakBlocks(data?.technicians, dayIso)
  const timedTasksByTechnician = useMemo(() => buildTimedTasksByTechnician(taskItems), [taskItems])

  const timeManagementByTechnician = useMemo(() => {
    const map = new Map<string, TimeManagementListItem[]>()
    if (!data?.technicians) return map
    for (const tech of data.technicians) {
      map.set(tech.technicianId, filterSchedulableTimeManagement(tech.timeManagementEntries ?? []))
    }
    return map
  }, [data?.technicians])

  const { hasCollision } = useCollisionDetection(
    data?.technicians, breakBlocks, timedTasksByTechnician, timeManagementByTechnician, dayIso,
  )

  const { tryDurationsChange, collisionWarning } = useCollisionAwareDurations({
    draft,
    ghost,
    hasCollision,
    setDurations,
    excludeTaskId: taskId,
  })

  const [showModal, setShowModal] = useState(false)

  if (!draft) return null

  // draft.date is anfahrtStartIso; work start = anfahrtStart + travelDuration
  const workStartIso = new Date(
    new Date(draft.date).getTime() + draft.plannedTravelDuration * 60_000
  ).toISOString()
  const workDateTime = formatWorkDateTime(workStartIso)
  const canSave = !!draft.date && !!draft.assignedTechnicianId

  return (
    <>
      <div className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-slate-200 shadow-lg">
        <div className="flex items-center justify-between px-8 py-3">
          <div className="flex items-center gap-6 text-sm">
            <span className="text-slate-500">
              Aufgabe: <span className="font-medium text-slate-900">{taskTitle}</span>
            </span>
            <span className="text-slate-500">
              Techniker: <span className="font-medium text-slate-900">{technicianName ?? '—'}</span>
            </span>
            {workDateTime && (
              <span className="text-slate-500">
                {workDateTime.date}{' '}
                <span className="font-medium text-slate-900">{workDateTime.time}</span>
              </span>
            )}
          </div>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setShowModal(true)}
              className="p-1.5 rounded hover:bg-slate-100 transition-colors text-slate-500"
              title="Details bearbeiten"
            >
              <ChevronUp size={18} />
            </button>
            <button
              type="button"
              onClick={onCancel}
              disabled={isPending}
              className="px-5 py-2 rounded-[40px] border border-slate-300 text-slate-600 font-['Roboto'] text-[15px] font-medium hover:bg-slate-50 transition-colors disabled:opacity-50"
            >
              Abbrechen
            </button>
            <button
              type="button"
              onClick={onSave}
              disabled={isPending || !canSave}
              className="px-5 py-2 rounded-[40px] bg-[#124093] text-white font-['Roboto'] text-[15px] font-medium hover:brightness-110 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {isPending ? 'Speichern...' : 'Speichern'}
            </button>
          </div>
        </div>
      </div>

      <DraftTaskForm
        show={showModal}
        setShow={setShowModal}
        taskTitle={taskTitle}
        technicianName={technicianName}
        workDateTime={workDateTime}
        durations={durations}
        onDurationsChange={tryDurationsChange}
        collisionWarning={collisionWarning}
        onSave={onSave}
        canSave={canSave}
        isPending={isPending}
      />
    </>
  )
}
